<script>
import { mapGetters } from 'vuex';

export default {
  props: {
  	zoomControlPosition: {
  	  default: 3,
  	  type: Number
  	},
    pinSize: {
      type: Object,
      default() {
        return {
          width: 21,
          height: 28
        }
      }
    },
    mapIndex: {
      type: Number,
      default: 0
    },
    lotMaps: {
      type: Array,
      required: false
    }
  },

  data() {
    return {
      gm: {},
      markers: [],
      map: {},
      infoWindow: false,

      config: {
        layerId: 'my_custom_layer',
        icons: {
          'sold': '/images/site/maps/map-pin-red.svg',
          'model': '/images/site/maps/map-pin-blue.svg',
          'express': '/images/site/maps/map-pin.svg',
          'available-lot': '/images/site/maps/map-pin-green.svg',
          'available-home': '/images/site/maps/map-pin-green.svg',
        },
        map: {
          fullscreenControl: false,
          zoomControlOptions: {
            position: this.zoomControlPosition
          }
        }
      },
      residence: null
    };
  },

  computed: {
    ...mapGetters({
      appData: 'app/getData'
    }),

    lotMap() {
      const lotMaps = this.appData.lot_maps || this.lotMaps;
      return lotMaps ? lotMaps[this.mapIndex] : null;

    },

    lots() {
      const lotMaps = this.appData.lot_maps || this.lotMaps;
      return lotMaps ? lotMaps[this.mapIndex].mapLots : null;
    },

    legend() {
      return this.appData.legend;
    }
  },

  methods: {
    initMap(map) {
      window.appData = this.appData;
      this.$gmaps().then(gm => {
        this.gm = gm;
        this.setMapInstance(map);
        this.initListeners();
        this.setLotMap();
      });
    },

    setMapInstance(map) {
      this.map = map;
    },

    initListeners() {
      this.gm.event.addListener(this.map, "zoom_changed", () => {
        this.closeInfoWindows();
      });
      var idle = this.gm.event.addListener(this.map, "idle", () => {
        if (this.map.getZoom() > 2) this.map.setZoom(2);
        this.gm.event.removeListener(idle);
      });

	  var map = this.map;

      // Put some limitations on how far the map can be dragged
      var lat_lng_bounds = new google.maps.LatLngBounds(
		new google.maps.LatLng(-60, -72), // Southwest
		new google.maps.LatLng(60, 72) // Northeast
	  );

      this.gm.event.addListener(this.map, "drag", () => {
      		if (lat_lng_bounds.contains(map.getCenter())) return;
      		// Out of bounds - Move the map back within the bounds
      		var center = map.getCenter();
      		var x = center.lng();
      		var y = center.lat();
      		var max_x = lat_lng_bounds.getNorthEast().lng();
      		var max_y = lat_lng_bounds.getNorthEast().lat();
      		var min_x = lat_lng_bounds.getSouthWest().lng();
      		var min_y = lat_lng_bounds.getSouthWest().lat();

      		if (x < min_x) x = min_x;
      		if (x > max_x) x = max_x;
      		if (y < min_y) y = min_y;
      		if (y > max_y) y = max_y;


      		map.setCenter(new google.maps.LatLng(y, x));
      });
    },

    setLotMap() {
      if(!this.lotMap) return;
      // Create a new ImageMapType layer.
      const layer = new this.gm.ImageMapType({
        name: this.config.layerId,
        getTileUrl: (coord, zoom) => {
          var url = this.lotMap.tiles_path
            .replace('{x}', coord.x)
            .replace('{y}', coord.y)
            .replace('{z}', zoom);
          return url;
        },
        tileSize: new this.gm.Size(256, 256),
        minZoom: 2,
        maxZoom: 4
      });

      this.map.mapTypes.set(this.config.layerId, layer);
      this.map.setMapTypeId(this.config.layerId);

      this.loadLots();
    },

    loadLots() {
      this.lots.forEach((lot) => {
        if(typeof lot.latitude === 'undefined') {
          return;
        }

        this.addLotMarker(lot);
      });
    },

    addLotMarker(lot) {
      const infoWindow = this.createInfoWindow();
      const icon = {
        url: this.config.icons[lot.status],
        scaledSize: new this.gm.Size(this.pinSize.width, this.pinSize.height)
      };

      const marker = new this.gm.Marker({
        icon,
        position: {
          lat: parseFloat(lot.latitude),
          lng: parseFloat(lot.longitude),
        },
        clickable: this.lotHasInfoWindow(lot),
        map: this.map,
        optimized: false,
        content: lot
      });

      // get info windows if flyout needed
      if (this.lotHasInfoWindow(lot)) {
        const template = this.formattedTemplate(lot);
        marker.addListener('mouseover', () => {
            if (this.infoWindow !== false) {
                this.infoWindow.close();
                this.infoWindow.setContent(template);
                this.infoWindow.open(marker.getMap(), marker);
              }
          });
          marker.addListener('mousedown', () => {
            if (this.infoWindow !== false) {
                this.infoWindow.close();
                this.infoWindow.setContent(template);
                this.infoWindow.open(marker.getMap(), marker);
              }
          });
      }

      this.markers.push(marker);
    },

    createInfoWindow: function() {
      if (!this.infoWindow) {
        this.infoWindow = new this.gm.InfoWindow({});
        google.maps.event.addListener(this.infoWindow, 'domready', function () {
            const parent = document.querySelector('.gm-style-iw').parentNode;
            parent.classList.add('custom-iw');
            parent.lastChild.classList.add('custom-close-iw');
            parent.firstChild.classList.add('custom-window-iw');
        });
      }

      return this.infoWindow;
    },

    lotHasInfoWindow(lot) {
      return lot.status === 'express' ||
        lot.status === 'model' ||
        lot.status === 'available-lot' ||
        lot.status === 'available-home';
    },

    loadInfoWindow(marker) {
      if(this.infoWindow.marker === marker && this.infoWindow.getMap()) {
        return;
      }

      this.infoWindow.marker = marker;

      this.closeInfoWindows();
    },
    formattedTemplate(lot) {
      const flag = this.setFlag(lot.status);
      const elevation = lot.cover;
      return `
          <div class="site-plan-flyout">
            <img src="${flag}" class="status-flag" alt="${lot.status}"/>
            <div class="content-wrap">
                <section class="site-plan-flyout__elevation col-xs-12 col-sm-4">
                    <div class="framed-image">
                        <div
                            class="framed-image-content"
                            style="background-image: url('${elevation}');'"
                        >
                            <img src="${elevation}" alt="" aria-hidden="true" />
                        </div>
                    </div>
                </section>
                <div class="col-xs-12 col-sm-8 right-column">
                    <p class="community-name">
                        ${lot.plan}
                    </p>
                    <ul class="site-plan-flyout__summary">
                        <li class="residence-sqft">
                          <span class="icon-sqft"></span>
                            ${lot.sqft_range}
                        </li>
                        <li class="residence-bed">
                          <span class="icon-bed"></span>
                            ${lot.beds_range}
                        </li>
                        <li class="residence-bath">
                          <span class="icon-bath"></span>
                            ${lot.baths_range}
                        </li>
                    </ul>
                    <p class="residence-price">
                      ${lot.price_range}
                      ${ lot.lot ?
                        `<span class="lot-number">
                        | Lot ${lot.lot}
                        </span>` :
                        ''
                      }
                    </p>
                </div>
            </div>
          <div class="cta row">
            <p class="subheader">${lot.short_description || ''}</p>
            <a class="button--white-orange button--large" href="${lot.url}">
              View
              <span class="icon-slider-next"></span>
            </a>
          </div>
        </div>
      `;
    },

    getIcon(lot) {
      const icon = this.config.icons[lot.status];
      const itemized = this.legend[lot.plan];

      if(itemized) {
        return `${icon}-${itemized}.svg`;
      }

      return `${icon}.svg`;
    },

    setFlag(status) {
      if(status === 'available-lot' || status === 'available-home') {
        return '/images/site/maps/flags/available-flag.svg';
      }
      if(status === 'sold') {
        return '/images/site/maps/flags/sold-flag.svg';
      }
      if(status === 'model') {
        return '/images/site/maps/flags/model-flag-siteplan.svg';
      }
      if(status === 'express') {
        return '/images/site/maps/flags/qmi-flag.svg';
      }
    },

    closeInfoWindows() {
      if (this.infoWindow) {
        this.infoWindow.close();
      }
    }
  }
};
</script>
